import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, Lock, Search, Shield, Trash2, Users } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  cn,
} from "@nexus/ui";
import type { AccessGroupItem } from "@/features/identity-hub/api/identity-hub.api";
import { catalogScopeMode } from "@/features/identity-hub/lib/access-scope";

const SEARCH_CLASS =
  "h-9 w-full rounded-md border border-input-border bg-background pl-9 pr-3 text-[13px] outline-none placeholder:text-muted-foreground/70 focus:border-ring focus:ring-1 focus:ring-ring/40";

export type OrgWildcardAddOption = {
  id: string;
  name: string;
  templateId: string;
};

export type OrgDrawerRolesTabProps = {
  orgName: string;
  roles: AccessGroupItem[];
  memberCountByRole: Map<string, number>;
  onJumpToRole?: (roleId: string) => void;
  /** Superadmin only. Lists active Wildcard roles that are not already on the org. */
  addRoleOptions?: OrgWildcardAddOption[];
  onAddWildcardRole?: (templateId: string) => void;
  /** True while a cold-assign is in flight. Add stays disabled until it settles. */
  addPending?: boolean;
  /** Superadmin only. The tab still hides remove on custom and Fixed roles. */
  onRemoveWildcardRole?: (roleId: string) => void;
  /** True while a cold-remove is in flight. Remove stays disabled until it settles. */
  removePending?: boolean;
  /** Live access id of the Wildcard role whose removal is in flight. */
  removingRoleId?: string | null;
  noRolesEmpty: ReactNode;
};

function isManagedCatalogRole(role: AccessGroupItem): boolean {
  return role.type === "SYSTEM" || catalogScopeMode(role) !== null;
}

function isWildcardRole(role: AccessGroupItem): boolean {
  return catalogScopeMode(role) === "WILDCARD";
}

function isArchivedWildcard(role: AccessGroupItem): boolean {
  return isWildcardRole(role) && role.template?.status === "ARCHIVED";
}

function archivedRemoveHint(
  t: (key: string, options: { name: string; defaultValue: string }) => string,
  name: string,
): string {
  return t("iam.orgDrawer.rolesTab.removeArchived", {
    name,
    defaultValue: "{{name}} is archived. Restore the catalog role before removing it from this organization.",
  });
}

export function OrgDrawerRolesTab({
  orgName,
  roles,
  memberCountByRole,
  onJumpToRole,
  addRoleOptions = [],
  onAddWildcardRole,
  addPending = false,
  onRemoveWildcardRole,
  removePending = false,
  removingRoleId = null,
  noRolesEmpty,
}: OrgDrawerRolesTabProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [confirmingRole, setConfirmingRole] = useState<AccessGroupItem | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(q) ||
        (role.description?.toLowerCase().includes(q) ?? false),
    );
  }, [roles, search]);

  return (
    <div className="flex flex-col gap-4 px-6 py-5" data-testid="org-drawer-roles-tab">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h4 className="text-[13px] font-semibold leading-[1.54] text-foreground">
            {t("iam.orgDrawer.rolesTab.settingsRowTitle", { orgName, defaultValue: "Roles in {{orgName}}" })}
          </h4>
          <p className="text-xs text-muted-foreground">
            {t(
              "iam.orgDrawer.rolesTab.settingsRowHint",
              "A role bundles permissions. Everyone in this org gets access through one role.",
            )}
          </p>
        </div>
        <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
          {onAddWildcardRole ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  iconStart={addPending ? undefined : "plus"}
                  disabled={addRoleOptions.length === 0 || addPending}
                  aria-busy={addPending || undefined}
                  data-testid="org-drawer-add-role"
                >
                  {addPending ? (
                    <Icon name="loader" color="inherit" className="animate-spin motion-reduce:animate-none" />
                  ) : null}
                  {addPending
                    ? t("iam.orgDrawer.rolesTab.addingRole", "Adding role…")
                    : t("iam.orgDrawer.rolesTab.addRole", "Add role")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {addRoleOptions.length === 0 ? (
                  <DropdownMenuItem disabled>
                    {t(
                      "iam.orgDrawer.rolesTab.addRoleEmpty",
                      "Every Wildcard role is already on this organization.",
                    )}
                  </DropdownMenuItem>
                ) : (
                  addRoleOptions.map((option) => (
                    <DropdownMenuItem
                      key={option.templateId}
                      disabled={addPending}
                      onClick={() => {
                        if (!addPending) onAddWildcardRole(option.templateId);
                      }}
                    >
                      {option.name}
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        <div className="relative w-full shrink-0 sm:w-[220px]">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("iam.orgDrawer.rolesTab.searchPlaceholder", "Search roles…")}
            className={SEARCH_CLASS}
            data-testid="org-drawer-roles-search"
          />
        </div>
        </div>
      </div>

      {roles.length === 0 ? (
        noRolesEmpty
      ) : (
      <ul className="flex flex-col gap-2">
        {filtered.length === 0 ? (
          <li className="py-6 text-center text-xs text-muted-foreground">
            {t("iam.orgDrawer.rolesTab.noSearchMatch", "No roles match your search.")}
          </li>
        ) : (
          filtered.map((role) => {
            const isManagedCatalog = isManagedCatalogRole(role);
            const memberCount = memberCountByRole.get(role.id) ?? role.memberCount ?? 0;
            const showRemove = !!onRemoveWildcardRole && isWildcardRole(role);
            const isArchived = isArchivedWildcard(role);
            const isRemovingThisRole = removePending && removingRoleId === role.id;
            const removeDisabled = isArchived || removePending;
            const removeLabel = isArchived
              ? archivedRemoveHint(t, role.name)
              : t("iam.orgDrawer.rolesTab.removeRole", {
                name: role.name,
                defaultValue: "Remove {{name}} from this organization",
              });
            return (
              <li
                key={role.id}
                className={cn(
                  "group/role flex items-center gap-3 rounded-lg border bg-background px-4 py-3 transition-colors hover:bg-muted",
                  isManagedCatalog ? "border-dashed border-input-border" : "border-border",
                )}
              >
                {onJumpToRole ? (
                  <button
                    type="button"
                    onClick={() => onJumpToRole(role.id)}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
                  >
                    <RoleCardBody
                      role={role}
                      memberCount={memberCount}
                      isManagedCatalog={isManagedCatalog}
                    />
                  </button>
                ) : (
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <RoleCardBody
                      role={role}
                      memberCount={memberCount}
                      isManagedCatalog={isManagedCatalog}
                    />
                  </div>
                )}
                {showRemove ? (
                  <>
                    {isArchived ? (
                      <span className="max-w-[11rem] shrink text-right text-xs leading-snug text-muted-foreground">
                        {t("iam.orgDrawer.rolesTab.removeArchivedHint", {
                          defaultValue: "Archived. Restore the catalog role before removing it.",
                        })}
                      </span>
                    ) : null}
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="min-h-11 min-w-11 shrink-0 text-destructive hover:text-destructive [@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0"
                      onClick={() => {
                        if (!removeDisabled) setConfirmingRole(role);
                      }}
                      disabled={removeDisabled}
                      title={isArchived ? removeLabel : undefined}
                      aria-busy={isRemovingThisRole || undefined}
                      aria-label={removeLabel}
                    >
                      {isRemovingThisRole ? (
                        <Icon name="loader" color="inherit" className="size-3.5 animate-spin motion-reduce:animate-none" />
                      ) : (
                        <Trash2 className="size-3.5" />
                      )}
                    </Button>
                  </>
                ) : (
                  <span className="size-7 shrink-0" aria-hidden />
                )}
                <ChevronRight
                  className="h-4 w-4 shrink-0 text-muted-foreground/70 transition-colors group-hover/role:text-muted-foreground"
                  aria-hidden
                />
              </li>
            );
          })
        )}
      </ul>
      )}
      <RemoveRoleDialog
        role={confirmingRole}
        memberCount={confirmingRole ? (memberCountByRole.get(confirmingRole.id) ?? confirmingRole.memberCount ?? 0) : 0}
        pendingInviteCount={confirmingRole?.pendingInviteCount ?? 0}
        onCancel={() => setConfirmingRole(null)}
        onConfirm={() => {
          if (!confirmingRole) return;
          onRemoveWildcardRole?.(confirmingRole.id);
          setConfirmingRole(null);
        }}
      />
    </div>
  );
}

function RemoveRoleDialog({
  role,
  memberCount,
  pendingInviteCount,
  onCancel,
  onConfirm,
}: {
  role: AccessGroupItem | null;
  memberCount: number;
  pendingInviteCount: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={role != null} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="mb-1 flex h-9 w-9 items-center justify-center rounded-lg bg-destructive/10">
            <Trash2 className="h-4 w-4 text-destructive" />
          </div>
          <DialogTitle>
            {t("iam.orgDrawer.rolesTab.removeConfirmTitle", "Are you sure you want to remove this role?")}
          </DialogTitle>
          <DialogDescription>
            {memberCount > 0 ? (
              <span className="mt-2 block rounded-md bg-destructive/10 px-3 py-2 font-medium text-destructive">
                {t("iam.orgDrawer.rolesTab.removeMemberWarning", {
                  count: memberCount,
                  defaultValue_one: "{{count}} person will lose access to this organization",
                  defaultValue_other: "{{count}} people will lose access to this organization",
                })}
              </span>
            ) : null}
            {pendingInviteCount > 0 ? (
              <span className="mt-2 block rounded-md bg-destructive/10 px-3 py-2 font-medium text-destructive">
                {t("iam.orgDrawer.rolesTab.removeInviteWarning", {
                  count: pendingInviteCount,
                  defaultValue_one: "{{count}} pending invite for this role will be cancelled.",
                  defaultValue_other: "{{count}} pending invites for this role will be cancelled.",
                })}
              </span>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button type="button" variant="destructive" size="sm" onClick={onConfirm}>
            {t("iam.orgDrawer.rolesTab.removeConfirmAction", "Remove role")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleCardBody({
  role,
  memberCount,
  isManagedCatalog,
}: {
  role: AccessGroupItem;
  memberCount: number;
  isManagedCatalog: boolean;
}) {
  const { t } = useTranslation();
  const RoleIcon = isManagedCatalog ? Lock : Shield;
  return (
    <>
      <div
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground transition-colors",
          !isManagedCatalog && "group-hover/role:bg-primary-subtle group-hover/role:text-primary",
        )}
      >
        <RoleIcon className="h-4 w-4" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-[13px] font-medium leading-tight text-foreground">{role.name}</span>
          {isManagedCatalog && (
            <span className="shrink-0 rounded-sm bg-border-subtle px-1.5 py-[3px] font-mono text-[9px] uppercase leading-none text-muted-foreground">
              {t("iam.orgDrawer.rolesTab.managedByAdge", "Managed by Adge")}
            </span>
          )}
        </div>
        {role.description ? (
          <p className="line-clamp-2 text-[11px] leading-[1.45] text-muted-foreground">{role.description}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-4">
        <span
          className="flex items-center gap-1 text-xs tabular-nums text-foreground"
          aria-label={t("iam.orgDrawer.rolesTab.memberCountAria", {
            count: memberCount,
            defaultValue_one: "{{count}} member",
            defaultValue_other: "{{count}} members",
          })}
        >
          <Users className="h-3 w-3 text-muted-foreground" aria-hidden />
          {memberCount}
        </span>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {t("iam.orgDrawer.rolesTab.permissionCount", {
            count: role.permissions.length,
            defaultValue_one: "{{count}} permission",
            defaultValue_other: "{{count}} permissions",
          })}
        </span>
      </div>
    </>
  );
}
